/* File: conferenceCalling.jsx
 * Path: ananunadm/src/components/chat/conferenceCalling.jsx
 *
 * Full-screen conference call UI. Supports audio/video conference with:
 * - WebRTC mesh peer connections
 * - Screen sharing, mute/camera toggles
 * - Participant grid with active speaker detection
 * - Minimize to floating widget
 * - Call timer
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
	Avatar, Button, Text, Flex, Stack, Group, Badge, Tooltip,
	ActionIcon, Paper, Box, ScrollArea, rem
} from '@mantine/core';
import {
	IconMicrophone, IconMicrophoneOff, IconPhone, IconPhoneOff,
	IconVideo, IconVideoOff, IconScreenShare, IconScreenShareOff,
	IconUsers, IconMinimize, IconUserMinus, IconUserPlus
} from '@tabler/icons-react';

import { useWebRTC } from '../../helpers/useWebRTC';
import { useMediaDevices } from '../../helpers/useMediaDevices';
import { useScreenShare } from '../../helpers/useScreenShare';
import { useCallSocket } from '../../helpers/useCallSocket';
import { useCallPermissions } from '../../helpers/useCallPermissions';
import { DEFAULT_USER_IMAGE_SRC, getUserImageSrc } from '../../helpers/uploadImage';
import * as API from '../../helpers/api';
import config from '../../config';
import GroupCallMemberPicker, { groupCallMembers } from './GroupCallMemberPicker';

const formatDuration = (seconds) => {
	const h = Math.floor(seconds / 3600);
	const m = Math.floor((seconds % 3600) / 60);
	const s = seconds % 60;
	if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
	return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

function ParticipantTile({ userId, userName: rawUserName, userImage, stream, isLocal, isMuted, isVideoOff, isSpeaking, isScreenSharing, lang, onRemove, canRemove }) {
	const userName = typeof rawUserName === 'object' ? (rawUserName?.[lang] || rawUserName?.en || String(userId)) : (rawUserName || String(userId));
	const videoRef = useRef(null);

	useEffect(() => {
		if (videoRef.current && stream) {
			videoRef.current.srcObject = stream;
		}
	}, [stream]);

	const hasVideo = stream && stream.getVideoTracks().length > 0 && !isVideoOff;

	return (
		<Paper
			radius="md"
			p={0}
			style={{
				position: 'relative',
				overflow: 'hidden',
				aspectRatio: '16/9',
				backgroundColor: '#1a1a2e',
				border: isSpeaking ? '2px solid #4dabf7' : '2px solid transparent',
				minWidth: 180,
				flex: 1,
			}}
		>
			{hasVideo ? (
				<video
					ref={videoRef}
					autoPlay
					playsInline
					muted={isLocal}
					style={{ width: '100%', height: '100%', objectFit: 'cover', transform: isLocal && !isScreenSharing ? 'scaleX(-1)' : 'none' }}
				/>
			) : (
				<Flex align="center" justify="center" h="100%">
					<Avatar
						size={64}
						radius="xl"
						src={userImage || DEFAULT_USER_IMAGE_SRC}
					/>
				</Flex>
			)}

			{/* Name badge */}
			<Box style={{ position: 'absolute', bottom: 8, left: 8, display: 'flex', gap: 4, alignItems: 'center' }}>
				<Badge size="xs" color={isSpeaking ? 'blue' : 'dark'} style={{ opacity: 0.9 }}>
					{isLocal ? `${userName} (You)` : userName}
				</Badge>
				{isMuted && <IconMicrophoneOff size={12} color="#fa5252" />}
				{isScreenSharing && <IconScreenShare size={12} color="#40c057" />}
			</Box>

			{/* Remove button for admins */}
			{!isLocal && canRemove && (
				<Tooltip label="Remove from call">
					<ActionIcon
						size="xs"
						color="red"
						variant="filled"
						style={{ position: 'absolute', top: 8, right: 8 }}
						onClick={() => onRemove && onRemove(userId)}
					>
						<IconUserMinus size={10} />
					</ActionIcon>
				</Tooltip>
			)}
		</Paper>
	);
}

export function ConferenceCalling({
	roomId,
	callType,
	socket,
	user,
	lang,
	initialParticipants = [],
	chatId,
	initialGroupMembers = [],
	isHost = false,
	onEnded,
	onMinimize,
	isMinimized = false,
}) {
	const isVideo = callType === 'conference_video';
	const permissions = useCallPermissions();
	const localVideoRef = useRef(null);

	// Participants state: userId -> { userId, userName, userImage, mutedAudio, disabledVideo, sharingScreen }
	const [participants, setParticipants] = useState(() => {
		const map = {};
		initialParticipants.forEach((p) => {
			map[String(p.userId)] = p;
		});
		return map;
	});

	const [elapsed, setElapsed] = useState(0);
	const startedAtRef = useRef(null);
	const timerRef = useRef(null);

	// Track users who left or were removed so host can re-invite them
	const [leftParticipants, setLeftParticipants] = useState([]);
	const [groupMembers, setGroupMembers] = useState(initialGroupMembers);
	const [invitePickerOpen, setInvitePickerOpen] = useState(false);

	useEffect(() => {
		if (!chatId) return;
		let cancelled = false;
		API.get({
			route: config.api.chat.chatGroup,
			query: { _id: chatId },
			populate: [{ path: 'users', select: '_id name uploads' }, { path: 'admins', select: '_id name uploads' }]
		}).then((result) => {
			if (cancelled) return;
			const chat = Array.isArray(result) ? result[0] : result?.items?.[0] || result;
			setGroupMembers(groupCallMembers(chat, user?._id));
		}).catch(() => {});
		return () => { cancelled = true; };
	}, [chatId, user?._id]);

	const { stream, streamRef, audioMuted, videoDisabled, getStream, toggleAudio, toggleVideo, stopStream } = useMediaDevices();
	const { isSharing, startScreenShare: startShare, stopScreenShare: stopShare } = useScreenShare({
		onStart: (screenStream) => {
			if (localVideoRef.current) localVideoRef.current.srcObject = screenStream;
			callSocketActions.startScreenShare(roomId);
		},
		onStop: () => {
			if (localVideoRef.current) localVideoRef.current.srcObject = streamRef.current;
			callSocketActions.stopScreenShare(roomId);
		},
	});

	const { peerConnections, remoteStreams, createOffer, closePeerConnection, closeAll } = useWebRTC({
		localStreamRef: streamRef,
		socket,
		roomId,
		onRemoteStream: (userId) => {
			// Safety net: add participant to state if they sent us a stream but weren't in initialParticipants
			setParticipants((prev) => {
				if (prev[String(userId)]) return prev;
				return {
					...prev,
					[String(userId)]: { userId: String(userId), userName: String(userId), mutedAudio: false, disabledVideo: !isVideo, sharingScreen: false },
				};
			});
		},
		onParticipantLeft: (userId) => {
			setParticipants((prev) => {
				const next = { ...prev };
				delete next[userId];
				return next;
			});
		},
	});

	// ─── Call socket event handlers ──────────────────────────────────────────────

	const resolveUserName = (name, fallback) =>
		typeof name === 'object' ? (name?.en || name?.ru || name?.hy || fallback) : (name || fallback);

	const handleJoined = useCallback(({ userId, userName, participants: existingList }) => {
		setParticipants((prev) => ({
			...prev,
			[String(userId)]: { userId: String(userId), userName: resolveUserName(userName, userId), mutedAudio: false, disabledVideo: !isVideo, sharingScreen: false },
		}));
		// As existing participant, offer to the new joiner
		createOffer(String(userId));
	}, [createOffer, isVideo]);

	const handleUserLeft = useCallback(({ userId, userName }) => {
		setParticipants((prev) => {
			const next = { ...prev };
			const leaving = next[String(userId)];
			if (leaving) {
				setLeftParticipants((lp) => [
					...lp.filter((p) => p.userId !== String(userId)),
					{ userId: String(userId), userName: leaving.userName || resolveUserName(userName, userId) },
				]);
			}
			delete next[String(userId)];
			return next;
		});
		closePeerConnection(String(userId));
	}, [closePeerConnection]);

	const handleEnded = useCallback(() => {
		cleanup();
		if (typeof onEnded === 'function') onEnded();
	}, []);

	const handleParticipantStatus = useCallback(({ userId, mutedAudio, disabledVideo, sharingScreen }) => {
		setParticipants((prev) => {
			if (!prev[String(userId)]) return prev;
			return {
				...prev,
				[String(userId)]: {
					...prev[String(userId)],
					...(mutedAudio !== undefined ? { mutedAudio } : {}),
					...(disabledVideo !== undefined ? { disabledVideo } : {}),
					...(sharingScreen !== undefined ? { sharingScreen } : {}),
				},
			};
		});
	}, []);

	const handleRemoved = useCallback(() => {
		cleanup();
		if (typeof onEnded === 'function') onEnded('removed');
	}, []);

	const callSocketActions = useCallSocket({
		socket,
		onJoined: handleJoined,
		onUserLeft: handleUserLeft,
		onEnded: handleEnded,
		onParticipantStatus: handleParticipantStatus,
		onRemoved: handleRemoved,
	});

	// ─── Init ────────────────────────────────────────────────────────────────────

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const mediaStream = await getStream({ audio: true, video: isVideo });
			if (!mediaStream || cancelled) return;
			if (localVideoRef.current) localVideoRef.current.srcObject = mediaStream;
			// Start timer
			startedAtRef.current = new Date();
			timerRef.current = setInterval(() => {
				setElapsed(Math.round((Date.now() - startedAtRef.current.getTime()) / 1000));
			}, 1000);
		})();
		return () => { cancelled = true; };
	}, []);

	// ─── Cleanup ─────────────────────────────────────────────────────────────────

	const cleanup = useCallback(() => {
		clearInterval(timerRef.current);
		stopStream();
		closeAll();
	}, [stopStream, closeAll]);

	useEffect(() => () => cleanup(), []);

	// ─── Actions ─────────────────────────────────────────────────────────────────

	const handleToggleAudio = () => {
		if (!permissions.canMuteSelf) return;
		toggleAudio();
		if (audioMuted) callSocketActions.unmuteAudio(roomId);
		else callSocketActions.muteAudio(roomId);
	};

	const handleToggleVideo = () => {
		toggleVideo();
		if (videoDisabled) callSocketActions.enableVideo(roomId);
		else callSocketActions.disableVideo(roomId);
	};

	const handleToggleScreenShare = async () => {
		if (!permissions.canScreenShare) return;
		if (isSharing) {
			await stopShare(peerConnections.current, streamRef.current);
		} else {
			await startShare(peerConnections.current);
		}
	};

	const handleReinvite = (targetUserId, targetUserName) => {
		callSocketActions.inviteUser(roomId, targetUserId);
		setLeftParticipants((lp) => lp.filter((p) => p.userId !== String(targetUserId)));
	};

	const inviteMembers = async (selectedIds) => {
		const results = await Promise.all(selectedIds.map((targetUserId) =>
			callSocketActions.inviteUser(roomId, targetUserId)));
		return results.every((result) => result?.ok);
	};

	const handleLeave = () => {
		callSocketActions.leaveCall(roomId);
		cleanup();
		if (typeof onEnded === 'function') onEnded();
	};

	const handleEndForAll = () => {
		callSocketActions.endCall(roomId);
		cleanup();
		if (typeof onEnded === 'function') onEnded();
	};

	const handleRemoveParticipant = (targetUserId) => {
		callSocketActions.removeParticipant(roomId, targetUserId);
	};

	// ─── Minimized floating widget ────────────────────────────────────────────────

	if (isMinimized) {
		return (
			<Paper
				shadow="xl"
				radius="md"
				p="xs"
				style={{
					position: 'fixed',
					bottom: 24,
					right: 24,
					zIndex: 9999,
					minWidth: 200,
					backgroundColor: '#1a1a2e',
					color: '#fff',
					cursor: 'pointer',
				}}
				onClick={() => typeof onMinimize === 'function' && onMinimize(false)}
			>
				<Flex align="center" gap="xs">
					<IconUsers size={16} color="#4dabf7" />
					<Text size="sm" color="white">Conference • {formatDuration(elapsed)}</Text>
					<Badge size="xs" color="green">{Object.keys(participants).length + 1}</Badge>
					<ActionIcon size="sm" color="red" variant="filled" onClick={(e) => { e.stopPropagation(); handleLeave(); }}>
						<IconPhoneOff size={12} />
					</ActionIcon>
				</Flex>
			</Paper>
		);
	}

	// ─── Full-screen UI ───────────────────────────────────────────────────────────

	const allParticipants = [
		{
			userId: String(user._id),
			userName: user?.name?.[lang] || 'You',
			userImage: getUserImageSrc(user, { targetWidth: 96 }),
			stream,
			isLocal: true,
			mutedAudio: audioMuted,
			disabledVideo: videoDisabled || !isVideo,
			sharingScreen: isSharing,
		},
		...Object.values(participants).map((p) => ({
			...p,
			stream: remoteStreams[String(p.userId)] || null,
			isLocal: false,
			userImage: null,
		})),
	];

	return (
		<>
		<GroupCallMemberPicker opened={invitePickerOpen} onClose={() => setInvitePickerOpen(false)}
			title="Add people to call" members={groupMembers}
			excludedIds={[user?._id, ...Object.keys(participants)]}
			onConfirm={inviteMembers} />
		<Flex
			direction="column"
			style={{
				position: 'fixed',
				inset: 0,
				zIndex: 9000,
				backgroundColor: '#0d1117',
				color: '#e6edf3',
			}}
		>
			{/* Header */}
			<Flex align="center" justify="space-between" p="sm" style={{ borderBottom: '1px solid #21262d' }}>
				<Group spacing="xs">
					<IconUsers size={18} color="#4dabf7" />
					<Text size="sm" fw={600} color="white">
						{isVideo ? 'Video' : 'Audio'} Conference
					</Text>
					<Badge size="sm" color="green">{allParticipants.length} participants</Badge>
				</Group>
				<Group spacing="xs">
					{(isHost || permissions.canInvite) && chatId && (
						<Button compact variant="light" leftIcon={<IconUserPlus size={16} />}
							onClick={() => setInvitePickerOpen(true)}>Add people</Button>
					)}
					<Text size="sm" color="dimmed">{formatDuration(elapsed)}</Text>
					<Tooltip label="Minimize">
						<ActionIcon variant="subtle" color="gray" onClick={() => typeof onMinimize === 'function' && onMinimize(true)}>
							<IconMinimize size={16} />
						</ActionIcon>
					</Tooltip>
				</Group>
			</Flex>

			{/* Participant grid */}
			<ScrollArea style={{ flex: 1, padding: '12px' }}>
				<Flex wrap="wrap" gap="sm" style={{ alignContent: 'flex-start' }}>
					{allParticipants.map((p) => (
						<ParticipantTile
							key={p.userId}
							userId={p.userId}
							userName={p.userName}
							userImage={p.userImage}
							stream={p.stream}
							isLocal={p.isLocal}
							isMuted={p.mutedAudio}
							isVideoOff={p.disabledVideo}
							isSpeaking={false}
							isScreenSharing={p.sharingScreen}
							lang={lang}
							canRemove={permissions.canRemoveParticipant && !p.isLocal}
							onRemove={handleRemoveParticipant}
						/>
					))}
				</Flex>
			</ScrollArea>

			{/* Left / removed participants — host can re-invite */}
			{leftParticipants.length > 0 && (isHost || permissions.canRemoveParticipant || permissions.canInvite) && (
				<Flex
					gap="xs"
					align="center"
					px="md"
					py="xs"
					style={{ borderTop: '1px solid #21262d', flexWrap: 'wrap', backgroundColor: '#0d1117' }}
				>
					<Text size="xs" color="dimmed" mr={4}>Left the call:</Text>
					{leftParticipants.map((lp) => (
						<Tooltip key={lp.userId} label={`Re-invite ${lp.userName}`}>
							<Button
								size="xs"
								variant="outline"
								color="blue"
								leftIcon={<IconUserPlus size={12} />}
								onClick={() => handleReinvite(lp.userId, lp.userName)}
							>
								{lp.userName}
							</Button>
						</Tooltip>
					))}
				</Flex>
			)}

			{/* Controls */}
			<Flex align="center" justify="center" gap="md" p="lg" style={{ borderTop: '1px solid #21262d' }}>
				<Tooltip label={audioMuted ? 'Unmute' : 'Mute'}>
					<ActionIcon
						size="xl"
						radius="xl"
						color={audioMuted ? 'red' : 'blue'}
						variant="filled"
						onClick={handleToggleAudio}
						disabled={!permissions.canMuteSelf}
					>
						{audioMuted ? <IconMicrophoneOff size={20} /> : <IconMicrophone size={20} />}
					</ActionIcon>
				</Tooltip>

				{isVideo && (
					<Tooltip label={videoDisabled ? 'Enable camera' : 'Disable camera'}>
						<ActionIcon
							size="xl"
							radius="xl"
							color={videoDisabled ? 'red' : 'blue'}
							variant="filled"
							onClick={handleToggleVideo}
						>
							{videoDisabled ? <IconVideoOff size={20} /> : <IconVideo size={20} />}
						</ActionIcon>
					</Tooltip>
				)}

				{permissions.canScreenShare && (
					<Tooltip label={isSharing ? 'Stop sharing' : 'Share screen'}>
						<ActionIcon
							size="xl"
							radius="xl"
							color={isSharing ? 'green' : 'blue'}
							variant="filled"
							onClick={handleToggleScreenShare}
						>
							{isSharing ? <IconScreenShareOff size={20} /> : <IconScreenShare size={20} />}
						</ActionIcon>
					</Tooltip>
				)}

				<Tooltip label="Leave call">
					<ActionIcon size="xl" radius="xl" color="orange" variant="filled" onClick={handleLeave}>
						<IconPhone size={20} />
					</ActionIcon>
				</Tooltip>

				{(permissions.canEndAny || (isHost && permissions.canEndOwn)) && (
					<Tooltip label="End for everyone">
						<ActionIcon size="xl" radius="xl" color="red" variant="filled" onClick={handleEndForAll}>
							<IconPhoneOff size={20} />
						</ActionIcon>
					</Tooltip>
				)}
			</Flex>
		</Flex>
		</>
	);
}
